// Shared 2D-plan viewport-culling helpers. Systems can skip drawing objects
// that are far outside the current viewport without duplicating the logic.
//
// A system stays in full control of ITS OWN per-object bounds and whether to
// use them -- this module only answers "is this AABB/point currently
// relevant to draw" given the current camera + canvas size.

import { getCanvasSize, screenToWorld } from './plan_renderer.js?v=20260904f';
import { isBomPlanRendering } from '../bom/bom_plan_context.js?v=20260904c';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';
import { boundsContain, boundsIntersect, validRenderBounds } from '../../plugin-sdk/render_bounds.js';

const DEFAULT_BOUNDS_PADDING_PX = 180;
const DEFAULT_POINT_PADDING_PX = 160;

// BOM- and print-plan capture must always render every object regardless of
// the on-screen camera framing (the exported document has its own layout,
// not the user's current pan/zoom) -- returning null tells callers to skip
// culling entirely for this render pass.
function currentRenderViewportBounds(paddingPx = DEFAULT_BOUNDS_PADDING_PX) {
  // The BOM capture flag alone also spans the final editor restore. Its scope
  // is cleared before that restore, which must already use viewport culling.
  if (isBomPlanRendering() || isPrintPlanCaptureInProgress()) return null;
  const { w, h } = getCanvasSize();
  const a = screenToWorld(-paddingPx, -paddingPx);
  const b = screenToWorld(w + paddingPx, h + paddingPx);
  const bounds = {
    minX: Math.min(a.x, b.x),
    minZ: Math.min(a.z, b.z),
    maxX: Math.max(a.x, b.x),
    maxZ: Math.max(a.z, b.z)
  };
  return validRenderBounds(bounds) ? bounds : null;
}

function pointVisibleForRender(point, renderBounds, paddingPx = DEFAULT_POINT_PADDING_PX) {
  if (!validRenderBounds(renderBounds) || !point
      || !Number.isFinite(point.x) || !Number.isFinite(point.z)) return true;
  return point.x >= renderBounds.minX - paddingPx
    && point.x <= renderBounds.maxX + paddingPx
    && point.z >= renderBounds.minZ - paddingPx
    && point.z <= renderBounds.maxZ + paddingPx;
}

export {
  boundsContain,
  boundsIntersect,
  currentRenderViewportBounds,
  pointVisibleForRender
};
